import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const AUTH_STORAGE_KEY = 'support-app-authenticated';
const USER_STORAGE_KEY = 'support-app-username';
const TOKEN_STORAGE_KEY = 'support-app-token';

interface AuthContextValue {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readStoredAuthState = () => {
  if (typeof window === 'undefined') {
    return { 
      isAuthenticated: false, 
      username: null as string | null,
      token: null as string | null,
    };
  }

  const storedFlag = window.localStorage.getItem(AUTH_STORAGE_KEY);
  const storedUsername = window.localStorage.getItem(USER_STORAGE_KEY);
  const storedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);

  return {
    isAuthenticated: storedFlag === 'true',
    username: storedUsername ?? null,
    token: storedToken ?? null,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [{ isAuthenticated, username }, setAuthState] = useState(() => {
    const state = readStoredAuthState();
    return { isAuthenticated: state.isAuthenticated, username: state.username };
  });
  const [isLoading, setIsLoading] = useState(true);

  const persistState = useCallback((
    authenticated: boolean, 
    name: string | null, 
    token: string | null
  ) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (authenticated) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, 'true');
      if (name) {
        window.localStorage.setItem(USER_STORAGE_KEY, name);
      }
      if (token) {
        window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
      }
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      window.localStorage.removeItem(USER_STORAGE_KEY);
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, []);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      const { token } = readStoredAuthState();
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/auth/verify', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAuthState({ isAuthenticated: true, username: data.username });
        } else {
          // Token invalid, clear state
          persistState(false, null, null);
          setAuthState({ isAuthenticated: false, username: null });
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        persistState(false, null, null);
        setAuthState({ isAuthenticated: false, username: null });
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [persistState]);

  const login = useCallback(async (
    inputUsername: string, 
    password: string
  ): Promise<boolean> => {
    const normalizedUsername = inputUsername.trim();

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: normalizedUsername,
          password,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAuthState({ isAuthenticated: true, username: data.username });
        persistState(true, data.username, data.token);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }, [persistState]);

  const logout = useCallback(async () => {
    const { token } = readStoredAuthState();
    
    try {
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      setAuthState({ isAuthenticated: false, username: null });
      persistState(false, null, null);
    }
  }, [persistState]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      username,
      login,
      logout,
      isLoading,
    }),
    [isAuthenticated, login, logout, username, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
